/**
 * Single source of truth for Cargo Heating "Segment" display labels.
 *
 * Display names match the New Voyage / plan form standardized labels.
 * API/filter payloads still use the legacy stored values via
 * `toCargoHeatingSegmentApiValue` / `expandCargoHeatingSegmentApiValues`.
 */

export const CARGO_HEATING_SEGMENT_OPTIONS: ReadonlyArray<{
  label: string;
  value: string;
}> = [
  { label: 'MR2', value: 'MR2' },
  { label: 'LR1 / Panamax', value: 'LR1 / Panamax' },
  { label: 'LR2 / Aframax', value: 'LR2 / Aframax' },
  { label: 'LR3 / Suezmax', value: 'LR3 / Suezmax' },
  { label: 'Intermediate / Coastal', value: 'Intermediate / Coastal' },
  { label: 'Handy(6W)', value: 'Handy(6W)' },
  { label: 'Handy(9W)', value: 'Handy(9W)' },
  { label: 'VLCC', value: 'VLCC' },
];

/** Display labels for Segment dropdowns / multi-selects. */
export const CARGO_HEATING_SEGMENTS: string[] =
  CARGO_HEATING_SEGMENT_OPTIONS.map((opt) => opt.label);

const LABEL_BY_KEY = new Map<string, string>();
const API_BY_LABEL = new Map<string, string>();
const ALIASES_BY_LABEL = new Map<string, string[]>();

function segmentKey(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_/()\\.-]+/g, '');
}

const EXTRA_ALIASES: Record<string, string[]> = {
  MR2: ['MR', 'MR2', 'Medium Range'],
  'LR1 / Panamax': ['Panamax', 'LR1', 'LR1/Panamax', 'LR1 / Panamax'],
  'LR2 / Aframax': ['Aframax', 'LR2', 'LR2/Aframax', 'LR2 / Aframax'],
  'LR3 / Suezmax': ['Suezmax', 'LR3', 'LR3/Suezmax', 'LR3 / Suezmax'],
  'Intermediate / Coastal': [
    'Intermediate',
    'Coastal',
    'Intermediate / Coastal',
    'Intermediate/Coastal',
  ],
  'Handy(6W)': [
    'Handy(6W)',
    'Handy(1-6W)',
    'Handy (6W)',
    'Handy (1-6W)',
    'Handytankers',
  ],
  'Handy(9W)': [
    'Handy(9W)',
    'Handy(1-9W)',
    'Handy (9W)',
    'Handy (1-9W)',
    'Handymax',
  ],
  VLCC: ['VLCC'],
};

for (const opt of CARGO_HEATING_SEGMENT_OPTIONS) {
  API_BY_LABEL.set(opt.label, opt.value);
  const aliases = EXTRA_ALIASES[opt.label] ?? [opt.label, opt.value];
  ALIASES_BY_LABEL.set(opt.label, aliases);
  for (const alias of aliases) {
    LABEL_BY_KEY.set(segmentKey(alias), opt.label);
  }
  LABEL_BY_KEY.set(segmentKey(opt.label), opt.label);
  LABEL_BY_KEY.set(segmentKey(opt.value), opt.label);
}

/** Map a display label (or any alias) to the legacy API value. */
export function toCargoHeatingSegmentApiValue(segment: string): string {
  const label = canonicalizeCargoHeatingSegment(segment);
  if (label && API_BY_LABEL.has(label)) {
    return API_BY_LABEL.get(label)!;
  }
  return String(segment ?? '').trim();
}

/** Expand selected display labels into API values (legacy spellings). */
export function mapCargoHeatingSegmentsToApi(segments: string[]): string[] {
  return (segments || [])
    .map((seg) => toCargoHeatingSegmentApiValue(seg))
    .filter(Boolean);
}

/** Normalize any segment spelling onto the standardized display label. */
export function canonicalizeCargoHeatingSegment(
  value: string | null | undefined,
): string {
  const raw = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!raw) {
    return '';
  }
  return LABEL_BY_KEY.get(segmentKey(raw)) || raw;
}

/** True when item segment matches any selected display label (alias-aware). */
export function matchesCargoHeatingSegment(
  itemSegment: string | null | undefined,
  selectedLabels: string[],
): boolean {
  if (!selectedLabels?.length) {
    return true;
  }
  const itemLabel = canonicalizeCargoHeatingSegment(itemSegment);
  if (!itemLabel) {
    return false;
  }
  return selectedLabels.some((selected) => {
    if (selected === 'All') {
      return true;
    }
    return canonicalizeCargoHeatingSegment(selected) === itemLabel;
  });
}
